import { TFunction } from "i18next";

import { DashboardSetupResponseDto } from "../../model/dashboard/dto/DashboardSetupResponseDto";
import { DashboardTodayEntrySourceDto } from "../../model/dashboard/dto/DashboardTodayEntrySourceDto";
import { DashboardMode } from "../../model/dashboard/types/DashboardMode.enum";
import { DashboardSetupStep } from "../../model/dashboard/types/DashboardSetupStep.enum";

export const ANDROID_APP_URL =
  "https://play.google.com/store/apps/details?id=com.atiragrup.atiraspacemobile";

export const DASHBOARD_SETUP_STEPS: DashboardSetupStep[] = [
  DashboardSetupStep.FORM,
  DashboardSetupStep.ENTRY,
  DashboardSetupStep.WHATSAPP,
  DashboardSetupStep.TEAM,
  DashboardSetupStep.DEAL,
  DashboardSetupStep.TASK,
];

/** How many columns the app card should span to fill the rest of its row.
 * A featured form card fills the first column for two rows, so those rows
 * have one cell less beside it. */
export const getAppCardSpan = (
  cards: number,
  tallFirst: boolean,
  columns: number,
) => {
  if (!tallFirst) {
    return columns - (cards % columns);
  }

  const others = cards - 1;
  const besideForm = 2 * (columns - 1);

  if (others < besideForm) {
    return columns - 1 - (others % (columns - 1));
  }

  return columns - ((others - besideForm) % columns);
};

export const isSetupComplete = (setup: DashboardSetupResponseDto) =>
  DASHBOARD_SETUP_STEPS.every((step) => setup.steps[step]);

/** The mode the user picked wins. Until they pick one, an account still
 * setting up gets the getting-started board. */
export const resolveDashboardMode = (config: {
  isMember: boolean;
  picked?: DashboardMode | null;
  setup: DashboardSetupResponseDto | null;
}): DashboardMode | null => {
  if (config.isMember) {
    return DashboardMode.TODAY;
  }

  if (config.picked) {
    return config.picked;
  }

  if (!config.setup) {
    return null;
  }

  return isSetupComplete(config.setup)
    ? DashboardMode.TODAY
    : DashboardMode.SETUP;
};

const ENTRY_ORIGIN_LABELS: Record<string, string> = {
  "whatsapp-meta": "dashboard.today.sources.whatsapp",
  facebook: "dashboard.today.sources.facebook",
  import: "dashboard.today.sources.import",
};

export const getEntrySourceLabel = (
  source: DashboardTodayEntrySourceDto,
  t: TFunction,
) => {
  if (source.formTitle) {
    return source.formTitle;
  }

  if (!source.origin) {
    return t("dashboard.today.sources.manual");
  }

  return ENTRY_ORIGIN_LABELS[source.origin]
    ? t(ENTRY_ORIGIN_LABELS[source.origin])
    : source.origin;
};
